import React, { useState } from "react";
import { Box, IconButton, Chip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { User } from "../../types/user";
import { useHasPermission } from "../../hooks/useHasPermission";
import GenericTable, { ColumnDefinition } from "../common/generic-table";
import SearchBar from "../common/search-bar";
import "../../assets/styles/common/table.css";

interface UserTableProps {
  users: User[];
  onEdit: (user: User) => void;
  onDelete: (user: User) => void;
  loading?: boolean;
}

const UserTable: React.FC<UserTableProps> = ({
  users,
  onEdit,
  onDelete,
  loading = false,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const canUpdateUser = useHasPermission("accessmanagement", "update");
  const canDeleteUser = useHasPermission("accessmanagement", "delete");

  // Filter users based on search query
  const filteredUsers = users.filter((user) => {
    const query = searchQuery.toLowerCase();
    return (
      user.email.toLowerCase().includes(query)
      // user.firstName.toLowerCase().includes(query) ||
      // user.lastName.toLowerCase().includes(query) ||
      // user.role.toLowerCase().includes(query)
    );
  });

  // Define columns for the table
  const columns: ColumnDefinition<User>[] = [
    {
      id: "name",
      label: "Name",
      format: (user) => `${user.displayName}`,
    },
    {
      id: "email",
      label: "Email",
      format: (user) => user.email,
    },
    {
      id: "status",
      label: "Status",
      format: (user) => (
        <Chip
          label={user.status.toUpperCase()}
          size="small"
          className={user.status === "enabled" ? "status-chip-enabled" : "status-chip-disabled"}
        />
      ),
    },
  ];

  return (
    <Box>
      <SearchBar
        placeholder="Search users by name, email, or role..."
        value={searchQuery}
        onChange={setSearchQuery}
      />
      {/* Table */}
      <GenericTable
        columns={columns}
        data={filteredUsers}
        getRowKey={(user) => user.id}
        loading={loading}
        emptyMessage={
          searchQuery
            ? "No users found matching your search"
            : "No users available"
        }
        actionsColumn={{
          label: "Actions",
          align: "right",
          render: (user) => (
            <Box className="user-table-actions">
              {canUpdateUser && (
                <IconButton
                  onClick={() => onEdit(user)}
                  className="user-table-edit-button"
                >
                  <EditIcon />
                </IconButton>
              )}
              {canDeleteUser && (
                <IconButton
                  onClick={() => onDelete(user)}
                  className="user-table-delete-button"
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
          ),
        }}
        showResultsCount={true}
      />
    </Box>
  );
};

export default UserTable;
